import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { notifySuccess, notifyError } from '../../utils/notifications';
import { X, Scale, CheckCircle, AlertTriangle, Package } from 'lucide-react';

/**
 * ControleFinalModal — composant partage finalisation OF
 *
 * Workflow :
 *  1. Load GET /atelier/controle-final/<of_id>/preview
 *     -> recupere dernier_poids_bijou + poids_pierres + meta
 *  2. Pre-remplit input poids_final avec dernier_poids
 *  3. Live-calcul poids_total = poids_final + poids_pierres
 *  4. POST /atelier/controle-final/<of_id> avec poids_final + notes
 *  5. Toast success avec id_produit + reference_article + cout reel
 *
 * Remplace prompt() + alert() utilises avant dans Atelier.js (2 instances).
 */

const ControleFinalModal = ({ ofId, onClose, onSuccess, isAdmin = false }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const [poidsFinal, setPoidsFinal] = useState('');
  const [poidsTotal, setPoidsTotal] = useState('');
  const [poidsPierres, setPoidsPierres] = useState(0);
  const [notes, setNotes] = useState('');
  const [force, setForce] = useState(false);
  const [forceVisible, setForceVisible] = useState(false);
  const [error, setError] = useState(null);

  // Load preview
  useEffect(() => {
    if (!ofId) return;
    api.get(`/atelier/controle-final/${ofId}/preview`)
      .then(r => {
        const d = r.data;
        setPreview(d);
        setPoidsFinal(d.dernier_poids_bijou != null ? d.dernier_poids_bijou.toString() : '');
        setPoidsPierres(d.poids_pierres || 0);
        setPoidsTotal(d.poids_total_estime != null ? d.poids_total_estime.toString() : '');
      })
      .catch(err => {
        const msg = err.response?.data?.error || err.message || 'Impossible de charger preview';
        setPreviewError(msg);
        notifyError(msg);
      });
  }, [ofId]);

  // Recalcul poids_total quand poids_final change
  useEffect(() => {
    if (poidsFinal && !isNaN(parseFloat(poidsFinal))) {
      const total = parseFloat(poidsFinal) + parseFloat(poidsPierres || 0);
      setPoidsTotal(total.toFixed(3));
    }
  }, [poidsFinal, poidsPierres]);

  const handleSubmit = async () => {
    if (!poidsFinal || parseFloat(poidsFinal) <= 0) {
      setError('Poids final or > 0 requis');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const url = `/atelier/controle-final/${ofId}${force ? '?force=true' : ''}`;
      const res = await api.post(url, {
        poids_final: parseFloat(poidsFinal),
        poids_total: poidsTotal ? parseFloat(poidsTotal) : null,
        notes: notes || null,
        force: force,
      });
      const d = res.data;

      const cout = d.cout_reel_total != null
        ? parseFloat(d.cout_reel_total).toFixed(2) + ' DH'
        : '—';
      notifySuccess(
        `Bijou ${d.id_produit || '?'} (ref ${d.reference_article || '?'}) finalise → stock centrale. Cout : ${cout}`,
        { duration: 10000 }
      );

      onSuccess?.(d);
      onClose?.();
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data || {};
      const msg = data.message || data.error || err.message || 'Erreur';
      if (status === 400 && data.error === 'etapes_incompletes') {
        setError(msg);
        setForceVisible(true);
      } else {
        setError(msg);
        notifyError(msg, { code: data.code, status });
      }
    } finally {
      setLoading(false);
    }
  };

  if (previewError) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 max-w-md w-full">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-bold">Erreur chargement</h3>
              <p className="text-sm text-gray-600 mt-1">{previewError}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-full bg-gray-200 hover:bg-gray-300 py-2 rounded">
            Fermer
          </button>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="text-sm text-gray-600 mt-3">Chargement preview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Scale className="h-6 w-6 text-emerald-600" />
            Finaliser le bijou
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Meta bijou */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
          <div className="font-semibold text-blue-900">{preview.bijou_nom || '(sans nom)'}</div>
          <div className="text-gray-700 text-xs mt-1">
            Ref article : <strong>{preview.reference_article || '—'}</strong>
            <span className="mx-2">•</span>
            ID Produit : <strong>{preview.id_produit || '—'}</strong>
          </div>
          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <Package className="h-3 w-3" />
            OF {preview.numero_ordre} → Stock centrale (boutique #{preview.boutique_centrale_id})
          </div>
        </div>

        <div className="space-y-3">
          {/* Poids final or */}
          <div>
            <label className="block text-sm font-semibold mb-1">
              Poids final or (g) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.001"
              value={poidsFinal}
              onChange={e => setPoidsFinal(e.target.value)}
              className="w-full p-2 border-2 border-emerald-300 focus:border-emerald-500 rounded outline-none text-lg font-semibold"
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-1">
              Pre-rempli source <strong>{preview.source_poids}</strong> ({preview.dernier_poids_bijou}g).
              Peser piece et corriger si besoin.
            </p>
          </div>

          {/* Poids total */}
          <div>
            <label className="block text-sm font-semibold mb-1">
              Poids total (g)
              <span className="text-xs text-gray-500 font-normal ml-1">— or + pierres</span>
            </label>
            <input
              type="number"
              step="0.001"
              value={poidsTotal}
              onChange={e => setPoidsTotal(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            />
            <p className="text-xs text-gray-500 mt-1">
              Pierres : {poidsPierres}g (auto-calcul : poids_or + poids_pierres). Modifier si autres composants.
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold mb-1">Notes (optionnel)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows="2"
              className="w-full p-2 border border-gray-300 rounded text-sm"
              placeholder="Observations finales..."
            />
          </div>

          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              {error}
            </div>
          )}

          {forceVisible && isAdmin && (
            <label className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 p-2 rounded">
              <input
                type="checkbox"
                checked={force}
                onChange={e => setForce(e.target.checked)}
              />
              <span className="font-semibold text-amber-900">Forcer la finalisation (admin)</span>
            </label>
          )}
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={handleSubmit}
            disabled={loading || !poidsFinal}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded font-semibold flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Finalisation...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5" />
                Finaliser et envoyer en stock
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-3 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default ControleFinalModal;
